import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { apiRequest } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export interface Product {
  id: string;
  name: string;
  price: string;
  category: string;
  imageUrl?: string;
  description?: string;
  sourceUrl?: string;
}

interface ProductContextType {
  products: Product[];
  addProducts: (newProducts: Product[]) => Promise<void>;
  removeProduct: (id: string) => Promise<void>;
  clearProducts: () => Promise<void>;
  refreshProducts: () => Promise<void>;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export function ProductProvider({ children }: { children: ReactNode }) {
  const [products, setProducts] = useState<Product[]>([]);
  const { user, isAuthenticated } = useAuth();

  const refreshProducts = useCallback(async () => {
    if (!isAuthenticated || !user?.storeId) {
      setProducts([]);
      return;
    }
    try {
      const data = await apiRequest<{ products: Product[] }>(`/products?storeId=${user.storeId}`);
      setProducts(data.products || []);
    } catch {
      setProducts([]);
    }
  }, [isAuthenticated, user?.storeId]);

  useEffect(() => {
    refreshProducts();
  }, [refreshProducts]);

  const addProducts = async (newProducts: Product[]) => {
    try {
      const result = await apiRequest<{ products: Product[] }>("/products", {
        method: "POST",
        body: JSON.stringify({ products: newProducts }),
      });
      setProducts((prev) => [...prev, ...(result.products || [])]);
    } catch {
      setProducts((prev) => {
        const merged = [...prev];
        for (const p of newProducts) {
          if (!merged.find((e) => e.id === p.id)) merged.push(p);
        }
        return merged;
      });
    }
  };

  const removeProduct = async (id: string) => {
    try {
      await apiRequest(`/products/${id}`, { method: "DELETE" });
    } catch {}
    setProducts((prev) => prev.filter((p) => p.id !== id));
  };

  const clearProducts = async () => {
    try {
      await apiRequest("/products", { method: "DELETE" });
    } catch {}
    setProducts([]);
  };

  return (
    <ProductContext.Provider value={{ products, addProducts, removeProduct, clearProducts, refreshProducts }}>
      {children}
    </ProductContext.Provider>
  );
}

export function useProducts() {
  const ctx = useContext(ProductContext);
  if (!ctx) throw new Error("useProducts must be used within ProductProvider");
  return ctx;
}
